import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

/**
 * Custom hook for checking user permissions and fetching team member info
 * @param {string} userId - Target user ID to check permissions for
 * @returns {Object} Permission state and team member info
 */
export const usePermissionCheck = (userId) => {
  const { currentUser, getUserInfo, getUserSubordinates, hasPermission, PERMISSIONS } = useAuth();
  const [hasViewPermission, setHasViewPermission] = useState(false);
  const [teamMemberName, setTeamMemberName] = useState('');
  const [permissionError, setPermissionError] = useState('');
  const [loading, setLoading] = useState(true);

  // Check if the current user has permission to view this user's data
  const canViewData = useCallback(async (targetUserId) => {
    if (!currentUser || !targetUserId) {
      logger.info('[usePermissionCheck]', 'No current user or target user');
      return false;
    }

    // Users can always view their own data
    if (currentUser.id === targetUserId) {
      logger.info('[usePermissionCheck]', 'User viewing own data');
      return true;
    }

    // Admin users can view all user data
    if (currentUser.role === 'admin') {
      logger.info('[usePermissionCheck]', 'Admin user has access to all user data');
      return true;
    }

    // Check if user has subordinate viewing permissions
    const hasSubordinatePermission = hasPermission(PERMISSIONS.VIEW_SUBORDINATE_TRAINING) ||
                                      hasPermission(PERMISSIONS.VIEW_SUBORDINATE_CV) ||
                                      hasPermission(PERMISSIONS.VIEW_SUBORDINATE_JOB_SKILLS) ||
                                      hasPermission(PERMISSIONS.VIEW_ALL_USER_DATA);
    if (!hasSubordinatePermission) {
      logger.info('[usePermissionCheck]', 'User lacks subordinate viewing permissions');
      return false;
    }

    // Check if the target user is in the current user's team
    try {
      const subordinates = await getUserSubordinates(currentUser.id);
      const isSubordinate = subordinates.some(sub => sub.id === targetUserId);
      logger.info('[usePermissionCheck]', 'Permission check result:', {
        targetUserId,
        isSubordinate,
        subordinatesCount: subordinates.length
      });
      return isSubordinate;
    } catch (error) {
      logger.error('[usePermissionCheck]', 'Error checking subordinates:', error);
      return false;
    }
  }, [
    currentUser,
    hasPermission,
    PERMISSIONS.VIEW_ALL_USER_DATA,
    PERMISSIONS.VIEW_SUBORDINATE_CV,
    PERMISSIONS.VIEW_SUBORDINATE_JOB_SKILLS,
    PERMISSIONS.VIEW_SUBORDINATE_TRAINING,
    getUserSubordinates
  ]);

  // Fetch team member information
  const fetchTeamMemberInfo = useCallback(async (targetUserId) => {
    if (!targetUserId || targetUserId === currentUser?.id) {
      setTeamMemberName('');
      return;
    }

    try {
      const userInfo = await getUserInfo(targetUserId);
      if (userInfo) {
        setTeamMemberName(userInfo.username || userInfo.email || 'Unknown User');
        logger.info('[usePermissionCheck]', 'Fetched team member info:', userInfo.username);
      }
    } catch (error) {
      logger.error('[usePermissionCheck]', 'Error fetching team member info:', error);
      setTeamMemberName('Unknown User');
    }
  }, [currentUser?.id, getUserInfo]);

  // Check permission and fetch data
  const checkPermissionAndFetch = useCallback(async () => {
    if (!userId) {
      logger.info('[usePermissionCheck]', 'No userId provided');
      setLoading(false);
      return;
    }

    setLoading(true);
    setPermissionError('');

    try {
      const hasPermission = await canViewData(userId);
      setHasViewPermission(hasPermission);

      if (hasPermission) {
        await fetchTeamMemberInfo(userId);
        logger.info('PERMISSION_CHECK', 'Permission granted for viewing user data', {
          currentUserId: currentUser?.id,
          targetUserId: userId
        });
      } else {
        setPermissionError('You do not have permission to view this user\'s data');
        logger.warn('PERMISSION_CHECK', 'Permission denied for viewing user data', {
          currentUserId: currentUser?.id,
          targetUserId: userId
        });
      }
    } catch (error) {
      logger.error('[usePermissionCheck]', 'Error during permission check:', error);
      setPermissionError('Error checking permissions');
      setHasViewPermission(false);
    } finally {
      setLoading(false);
    }
  }, [userId, currentUser?.id, canViewData, fetchTeamMemberInfo]);

  // Run permission check when userId changes
  useEffect(() => {
    if (userId && currentUser) {
      checkPermissionAndFetch();
    }
  }, [userId, currentUser, checkPermissionAndFetch]);

  return {
    hasViewPermission,
    teamMemberName,
    permissionError,
    loading,
    checkPermissionAndFetch,
    canViewData
  };
};
